import React, { useEffect, useRef, useState } from 'react';
import CmsAPI from '../../../common/apis/CmsAPI';
import { useUserContext } from '../../context/UserContext';
import CommentsApprovals from '../CommentsApprovals/CommentsApprovals';
import { LoadingSpinner } from '../Icon/icons';
import styles from './CommentsTab.module.scss';
import cx from 'classnames';
import Toast from '../../../common/utils/Toast';
import { useTranslation } from 'react-i18next';
import { parseString } from '../../utils/stringHelper';

const CommentsTab = ({
  noCommentsMessage,
  updateComments,
  isCommentsLoading,
  storyId,
  comments,
  commentInit,
  newComment,
  setNewComment,
  highlightFirst = true,
}) => {
  const [isNewCommentLoading, setIsNewCommentLoading] = useState(false);

  const { user } = useUserContext();

  const inputRef = useRef(null);

  const { t } = useTranslation();

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }

    return () => setNewComment(commentInit);
  }, []);

  const handleInputChange = event => {
    setNewComment({
      ...newComment,
      authorName: parseString(user.userFullName),
      username: user.username,
      content: event.target.value,
    });
  };

  const handleDeleteComment = async id => {
    try {
      const deleteCommentRes = await CmsAPI.deleteComment(storyId, id);
      if (deleteCommentRes.result === true) {
        updateComments();
      }
    } catch (err) {
      Toast.error(t('approvals.comment.toast.delete.error'));
    }
  };

  const handleEditComment = async comment => {
    try {
      await CmsAPI.updateComment(storyId, comment);
      updateComments();
    } catch (err) {
      Toast.error(t('approvals.comment.toast.edit.error'));
    }
  };

  const handleAddComment = async () => {
    try {
      setIsNewCommentLoading(true);
      await CmsAPI.insertComment(storyId, newComment);
      setNewComment(commentInit);
      updateComments();
    } catch (err) {
      Toast.error(t('approvals.comment.toast.add.error'));
    } finally {
      setIsNewCommentLoading(false);
    }
  };

  return (
    <div className={styles.approvalsCommentsContainer}>
      {noCommentsMessage && <p className={styles.noCommentsMessage}>{noCommentsMessage}</p>}
      <div className={styles.newCommentContainer}>
        <textarea
          ref={inputRef}
          className={styles.newComment}
          placeholder={t('approvals.comment.placeholder')}
          value={newComment?.content}
          onChange={handleInputChange}
        />
        <button
          className={cx(styles.newCommentBtn, {
            [styles.newCommentBtnDisabled]: isNewCommentLoading || !newComment?.content || newComment?.content.trim() === '',
          })}
          onClick={handleAddComment}
          disabled={isNewCommentLoading || !newComment?.content || newComment?.content.trim() === ''}
        >
          {isNewCommentLoading ? t('approvals.comment.saving') : t('approvals.comment.send')}
        </button>
      </div>
      {isCommentsLoading ? (
        <div className={styles.commentLoadingSpinner}>
          <LoadingSpinner theme="light" />
        </div>
      ) : (
        <div className={styles.approvalsComments}>
          {comments?.map((comment, index) => (
            <CommentsApprovals
              key={comment.id}
              comment={comment}
              user={comment.author}
              storyAuthor={user}
              handleDeleteComment={handleDeleteComment}
              handleEditComment={handleEditComment}
              isFirstComment={highlightFirst && comments.length - 1 === index}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentsTab;
